import { Badge, Box, Button, Center, Flex, Grid, GridItem, Image, Input, InputGroup, InputLeftElement, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Popover, PopoverTrigger, Spinner, Text, Textarea, Tooltip, } from '@chakra-ui/react';
import MyDiv from './onboardCandidateList.style';
import ArrowIcon from '../../../../assets/images/ActionArrow.svg'
import { CustomPagination, CustomToast, FilterPopover, CustomTable } from '../../../../components';
import { useEffect, useState } from 'react';
import { useFetch, ILazyParams } from '../../../../hooks/useFetch';
import { ICandidateList } from './modal';
import { SearchIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import * as routesNames from "../../../../constants/routes"
import FilterIcon from "../../../../assets/images/Filter.svg"
import { IFilterOptions } from '../../../../components/FilterCard/filterCard';
import { Column } from '../../../../components/CustomTable/customTable';
import { showDate } from '../../../../utilities/helper';

const OnboardCandidateList = () => {
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const [candidateList, setCandidateList] = useState<ICandidateList[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [textSearch, setTextSearch] = useState<string>("");
  const [reload, setReload] = useState(false);
  const [isBackoutModalOpen, setIsBackoutModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<ICandidateList | null>(null);
  const [backoutReason, setBackoutReason] = useState("");
  const navigate = useNavigate();
  const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });

  const [query, setQuery] = useState<{ lazyParams: ILazyParams, filter?: Record<string, number[]>; }>({
    lazyParams: { first: 0, rows: 20, page: 0, sortField: '', sortOrder: 1 },
    filter: {},
  });

  const pageCount = Math.ceil(totalCount / query.lazyParams.rows);
  const handlePageClick = ({ selected }: { selected: number }) => {
    setQuery(prev => {
      const updatedParams = { ...prev.lazyParams, page: selected, first: selected * prev.lazyParams.rows, };
      return { ...prev, lazyParams: updatedParams };
    });
  };

  const setRowsPerPage = (rows: number) => {
    setQuery((prev) => {
      const updatedParams = { ...prev.lazyParams, rows, page: 0, first: 0, };
      return { ...prev, lazyParams: updatedParams };
    });
  };

  const handleClick = (candidate: ICandidateList) => {
    navigate(routesNames.ONBOARDINGCANDIDATEPROFILE, { state: { candidate_Id: candidate.candidate_Id } })
  };

  const statusColorMap: Record<string, { bg: string; color: string }> = {
    Accepted: { bg: "#E5F2E5", color: "#008000" },
    Rejected: { bg: "#FFE5E5", color: "#FF0000" },
    Backout: { bg: "#FFE5E5", color: "#FF0000" },
    "Send Loi": { bg: "#FFFFE0", color: "#ae7306ff" },
    Applied: { bg: "#E5F0FF", color: "#0000FF" },
    Release: { bg: "#F0E5FF", color: "#530353ff" },
  };

  const loadCandidateList = async (filterValue = '') => {
    const queryParams = new URLSearchParams({
      lazyParams: JSON.stringify(query.lazyParams),
      search: filterValue.trim() === "" ? "" : filterValue,
      filter: JSON.stringify(query.filter ?? {}),
    })
    let res = await fetchApi("OfferAndOnBoarding/GetCandidateSelectionList", "GET", null, queryParams.toString(), "");
    if (res) {
      setCandidateList(res.candidateSelectionList);
      setTotalCount(res.totalRecords);
    }
  }

  async function loadEmployeeFilter() {
    const response = await fetchApi<IFilterOptions>('Filters/OpenJobFilter', 'GET', null, `statusFilter=${true}`, '');
    if (response) {
      setFilter(response);
    }
  }



  useEffect(() => {
    loadCandidateList();
    loadEmployeeFilter();
  }, [reload]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadCandidateList(textSearch);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [textSearch, query.lazyParams]);

  const isJoiningDateExceeded = (joiningDate?: string) => {
    if (!joiningDate) return false;

    const today = new Date();
    const doj = new Date(joiningDate);

    today.setHours(0, 0, 0, 0);
    doj.setHours(0, 0, 0, 0);

    return today > doj;
  };

  return (
    <MyDiv>
      <Box className="page_wrapper ">

        <Grid className='grid_container'>
          <GridItem colSpan={12}>
            <Box position="relative" minH="300px">
              <Flex className="page_heading flex_header" justify="space-between" align="center" mb={4}>
                <Text className="font-poppins text_medium text_3xl">Onboarding Candidate List</Text>
                <Flex gap={2} align="center">
                  <InputGroup maxW="200px">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input placeholder="Search Candidates" onChange={(e) => setTextSearch(e.target.value)} />
                  </InputGroup>
                  <Popover closeOnBlur>
                    <PopoverTrigger>
                      <Image src={FilterIcon} alt="Filter" className='icon_btn' />
                    </PopoverTrigger>
                    <FilterPopover filter={filter} onApplyFilter={(filters) => {
                      const _filter: Record<string, number[]> = {};
                      Object.keys(filters).forEach((key) => {
                        if (filters[key].length > 0) {
                          _filter[key.replace(' ', '')] = filters[key];
                        }
                      });
                      setQuery(prev => { return { ...prev, lazyParams: { ...prev.lazyParams, page: 0, first: 0 }, filter: _filter }; });
                    }}
                    />
                  </Popover>
                </Flex>
              </Flex>
              {loading ? (
                <Center position="absolute" top={0} left={0} right={0} bottom={0}>
                  <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
                </Center>
              ) : (
                <>
                  <CustomTable value={candidateList} emptyMessage="No Data Available">
                    <Column header="S. No" body={(_, index) => index + 1} />
                    <Column header="Name" field="candidate_Name" />
                    <Column header="Job Profile" field="job_profile_Name" />
                    <Column header="Payroll Company" field="payroll_Company_Name" />
                    <Column header="Branch" field="branchName" />
                    <Column header="Department" field="departmentName" />
                    <Column header="DOJ" body={(data: ICandidateList) => showDate(data.joining_Date)} />
                    <Column header="Status" body={(job: ICandidateList) => {
                      const isBackout = job.status_Name === "Backout";
                      const isJoined = job.status_Name === "Joined";
                      const isPendingforFeedback = job.status_Name === "Pending For Feedback";
                      const isHold = job.status_Name === "Hold";
                      const exceeded = isJoiningDateExceeded(job.joining_Date) && !isJoined && !isPendingforFeedback && !isHold && !isBackout;
                      const tooltipLabel = isBackout ? job.backout_Reason || "No reason provided" : exceeded ? "Click to mark candidate as Backout" : isJoined ? "Candidate already joined" : "";
                      return (
                        <Tooltip label={tooltipLabel} hasArrow isDisabled={!tooltipLabel} placement="top" >
                          <Badge
                            backgroundColor={statusColorMap[job.status_Name]?.bg || "#F5F5F5"}
                            color={statusColorMap[job.status_Name]?.color || "#008000"}
                            px={3}
                            py={1}
                            borderRadius="6px"
                            cursor={exceeded ? "pointer" : "default"}
                            transition="all 0.2s ease"
                            _hover={exceeded ? { transform: "scale(1.05)", boxShadow: "0 0 0 2px rgba(255,0,0,0.3)", } : {}}
                            onClick={() => {
                              if (exceeded) {
                                setSelectedCandidate(job);
                                setIsBackoutModalOpen(true);
                              }
                            }}
                          >
                            <Text className="font-poppins text_regular text_sm">
                              {job.status_Name}
                            </Text>
                          </Badge>
                        </Tooltip>
                      );
                    }}
                    />
                    <Column header="Action" body={(job: any) => (<Image src={ArrowIcon} alt="Action" cursor="pointer" onClick={() => handleClick(job)} />)} />
                  </CustomTable>

                </>)}
            </Box>
          </GridItem>
        </Grid>
        <CustomPagination pageCount={pageCount} handlePageClick={handlePageClick} rowsPerPage={query.lazyParams.rows} setRowsPerPage={setRowsPerPage} rowsPerPageOptions={[20, 50, 100, 200]} totalRecords={totalCount} currentPage={query.lazyParams.page} />

      </Box>

      <Modal isOpen={isBackoutModalOpen} onClose={() => setIsBackoutModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark Candidate as Backout</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea placeholder="Enter Backout Reason" value={backoutReason} onChange={(e) => setBackoutReason(e.target.value)} />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3}
              onClick={async () => {
                if (!backoutReason.trim()) {
                  addToast({ message: "Please enter reason", status: "error" });
                  return;
                }
                const response = await fetchApi("OfferAndOnBoarding/mark-Backout", "PUT", { candidate_Id: selectedCandidate?.candidate_Id, reason: backoutReason, });
                if (response) {
                  addToast({ message: "Candidate marked as Backout", status: "success" });
                  setIsBackoutModalOpen(false);
                  setBackoutReason("");
                  setReload(!reload);
                }
              }}
            >
              Submit
            </Button>
            <Button variant="ghost" onClick={() => setIsBackoutModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MyDiv>
  );
};

export default OnboardCandidateList;